/**
 * 菜单的展示属性。**只有样式，没有名字。**
 *
 * 名字一律走三语词条（`hooks/useMenu.ts` 取）：模块 `menu.module.<模块>`、分组
 * `menu.group.<模块>.<分组>`、页面 `menu.<键>`。服务端不再下发页面名（IAM-only 之后
 * 没有 labels 那一份了），词条是唯一来源，切语言才能跟着变。
 *
 * 键必须是 PRD 路由索引（muou_prd/MUOU-V1/dev-prd/00-global/00-route-index.md）
 * 里存在的 route，不得自造。
 *
 * 唯一例外是 `sys.package`（路径 `/iam/package`）：它由
 * `muou-backend/muou-app-doc/design/10-身份与权限（iam）/10-09-权限码与权限包整体设计.md`
 * §1.2 规定，PRD 路由索引里还没有这一条；补进索引前不要按「不在索引里」删掉。
 */
export interface MenuUI {
  path: string;
  /** @ant-design/icons 的组件名 */
  icon: string;
  /** 同一分组内的排序 */
  order: number;
  /** 二级分组代码（模块内唯一），名字取词条 `menu.group.<模块>.<分组>`——分组不是权限键，对应 PRD「模块」列第二段 */
  group?: string;
  /**
   * 归属的一级模块，缺省按键前缀推断。
   *
   * 只有权限键前缀与 PRD 一级模块对不上的页面才需要写：`cert.*` 三页在 PRD 里是
   * **会员中心 · 证书管理**这个二级分组（原型 `NAV_ADMIN` 把它夹在注册管理与属会管理之间），
   * 而权限键的 `cert` 段只是证书这一族权限的前缀。不写这一条，`useMenu` 会按前缀把
   * 它们挂成第 9 个一级模块「证书管理」。
   */
  module?: string;
}

/** 一级模块：代码 → 展示。名字取词条 `menu.module.<代码>`；模块不是权限键，可以写在前端 */
export const MODULE_UI: Record<string, { icon: string; order: number }> = {
  acct:   { icon: 'IdcardOutlined',   order: 10 },
  member: { icon: 'TeamOutlined',     order: 20 },
  org:    { icon: 'ApartmentOutlined', order: 40 },
  ops:    { icon: 'SettingOutlined',  order: 50 },
  // 订单中心：MENU_UI 里没有一条 order.* 键（服务端从不下发），这个模块只靠
  // 下面 ALWAYS_VISIBLE_MENU 的 4 条常显页面撑起来，不是死配置。
  order:  { icon: 'FileTextOutlined', order: 60 },
  fin:    { icon: 'AccountBookOutlined', order: 70 },
  sys:    { icon: 'ToolOutlined',     order: 90 },
};

export const MENU_UI: Record<string, MenuUI> = {
  // ── 帐号中心 ──
  'acct.portal': { path: '/acct/portal', icon: 'UserOutlined', order: 1, group: 'mgt' },
  'acct.access': { path: '/acct/access', icon: 'SafetyOutlined', order: 2, group: 'mgt' },
  'acct.req': { path: '/acct/req', icon: 'InboxOutlined', order: 3, group: 'mgt' },
  'acct.policy': { path: '/acct/policy', icon: 'ControlOutlined', order: 4, group: 'mgt' },

  // ── 会员中心 ──
  'member.person.list':    { path: '/member/person-list',    icon: 'UserOutlined', order: 1, group: 'person' },
  'member.person.archive': { path: '/member/person-archive', icon: 'FolderOutlined', order: 2, group: 'person' },
  'member.person.audit':   { path: '/member/person-audit',   icon: 'AuditOutlined', order: 3, group: 'person' },
  'member.person.corr':    { path: '/member/person-corr',    icon: 'EditOutlined', order: 4, group: 'person' },
  'member.list':           { path: '/member/list',           icon: 'TeamOutlined', order: 1, group: 'mgt' },
  'member.audit.join':     { path: '/member/join-audit',     icon: 'AuditOutlined', order: 2, group: 'mgt' },
  'member.audit.renew':    { path: '/member/renew-audit',    icon: 'AuditOutlined', order: 3, group: 'mgt' },
  'member.type':           { path: '/member/type',           icon: 'TagsOutlined', order: 4, group: 'mgt' },
  'member.qual.list':      { path: '/member/qual-list',      icon: 'IdcardOutlined', order: 1, group: 'qual' },
  'member.qual.audit':     { path: '/member/qual-audit',     icon: 'AuditOutlined', order: 2, group: 'qual' },
  'member.qual.renew':     { path: '/member/qual-renew',     icon: 'AuditOutlined', order: 3, group: 'qual' },
  'member.qual.clubjoin':  { path: '/member/qual-clubjoin',  icon: 'AuditOutlined', order: 4, group: 'qual' },
  'member.qual.transfer':  { path: '/member/qual-transfer',  icon: 'SwapOutlined', order: 5, group: 'qual' },
  'member.qual.type':      { path: '/member/qual-type',      icon: 'TagsOutlined', order: 6, group: 'qual' },

  // ── 证书管理：会员中心的二级分组，不是一级模块 ──
  // PRD 原型 NAV_ADMIN 把「证书管理」夹在注册管理与属会管理之间，注释写着
  // 「证书是注册的前置条件」。权限键前缀是 `cert`，单靠前缀会被挂成第 9 个一级模块，
  // 所以显式 module: 'member'；这里的位置同时决定分组顺序（组间 order 打平时按插入序）。
  'cert.type':             { path: '/member/cert-type',      icon: 'TagsOutlined', order: 1, group: 'cert', module: 'member' },
  'cert.list':             { path: '/member/cert-list',      icon: 'FileProtectOutlined', order: 2, group: 'cert', module: 'member' },
  'cert.audit':            { path: '/member/cert-audit',     icon: 'AuditOutlined', order: 3, group: 'cert', module: 'member' },

  'member.club.list':      { path: '/member/club-list',      icon: 'BankOutlined', order: 1, group: 'club' },
  'member.club.audit':     { path: '/member/club-audit',     icon: 'AuditOutlined', order: 2, group: 'club' },
  'member.club.upgrade':   { path: '/member/club-upgrade',   icon: 'RiseOutlined', order: 3, group: 'club' },
  'member.club.score':     { path: '/member/club-score',     icon: 'CalculatorOutlined', order: 4, group: 'club' },
  'member.club.level':     { path: '/member/club-level',     icon: 'TrophyOutlined', order: 5, group: 'club' },

  // ── 组织会务 ──
  // 三组按 PRD 原型侧栏（NAV_ADMIN：会务管理 / 组织管理 / 秘书处）排。分组顺序取组内最小
  // order（useMenu），所以每组占一个十位数段，组间不会跟着服务端下发顺序漂。
  // 会内公告的页面码是 `org.notice.view`（10-09 §1.2）：iam 按它算进 reachableRoutes，
  // 前端只认路由 `/org/notice` 译出的这个菜单键——页面可达不从 permissions 推。
  'org.notice':    { path: '/org/notice',    icon: 'SoundOutlined', order: 1, group: 'affairs' },
  'org.meeting':   { path: '/org/meeting',   icon: 'CalendarOutlined', order: 2, group: 'affairs' },
  'org.motion':    { path: '/org/motion',    icon: 'AuditOutlined', order: 3, group: 'affairs' },
  'org.todo':      { path: '/org/todo',      icon: 'BellOutlined', order: 4, group: 'affairs' },
  'org.info':      { path: '/org/info',      icon: 'BankOutlined', order: 21, group: 'mgt' },
  // org.cmt 是「委员会架构」，页面 /org/cmt；org.committee 是「委员名册」，页面 /org/committee。
  // 两页都由服务端 reachableRoutes 授出（iam 的 AdminLandingPage 各有一条）
  'org.cmt':       { path: '/org/cmt',       icon: 'ApartmentOutlined', order: 22, group: 'mgt' },
  'org.committee': { path: '/org/committee', icon: 'TeamOutlined', order: 23, group: 'mgt' },
  'org.cmtkind':   { path: '/org/cmtkind',   icon: 'TagsOutlined', order: 24, group: 'mgt' },
  'org.duty':      { path: '/org/duty',      icon: 'ProfileOutlined', order: 25, group: 'mgt' },
  'org.dept':      { path: '/org/dept',      icon: 'ApartmentOutlined', order: 31, group: 'secretariat' },
  // 职员帐号（10-03-15-01 的建号入口）：同 above，靠常显清单出现在侧边栏
  'org.staff':     { path: '/org/staff',     icon: 'IdcardOutlined', order: 32 },
  'org.job':       { path: '/org/job',       icon: 'ToolOutlined', order: 33, group: 'secretariat' },

  // ── 运营配置 ──
  'ops.site.tpl':     { path: '/ops/site-template', icon: 'LayoutOutlined', order: 1, group: 'site' },
  'ops.site.cfg':     { path: '/ops/site-config',   icon: 'SettingOutlined', order: 2, group: 'site' },
  'ops.site.home':    { path: '/ops/site-home',     icon: 'HomeOutlined', order: 3, group: 'site' },
  'ops.site.menu':    { path: '/ops/site-menu',     icon: 'MenuOutlined', order: 4, group: 'site' },
  'ops.site.article': { path: '/ops/site-article',  icon: 'FileTextOutlined', order: 5, group: 'site' },
  'ops.site.page':    { path: '/ops/site-page',     icon: 'BlockOutlined', order: 6, group: 'site' },
  // 顺序照 PRD 路由索引：发送消息 → 通知类型 → 消息模板 → 群发任务 → 发送记录 → 接收分组
  'ops.msg.send':     { path: '/ops/msg-send',      icon: 'SendOutlined', order: 0, group: 'msg' },
  'ops.msg.kind':     { path: '/ops/msg-kind',      icon: 'AppstoreOutlined', order: 1, group: 'msg' },
  'ops.msg.tpl':      { path: '/ops/msg-template',  icon: 'MailOutlined', order: 2, group: 'msg' },
  'ops.msg.bulk':     { path: '/ops/msg-bulk',      icon: 'SendOutlined', order: 3, group: 'msg' },
  'ops.msg.log':      { path: '/ops/msg-log',       icon: 'HistoryOutlined', order: 4, group: 'msg' },
  'ops.msg.group':    { path: '/ops/msg-group',     icon: 'UsergroupAddOutlined', order: 5, group: 'msg' },
  // 字段库在表单配置前面：模板里的字段全部来自它（原型侧栏顺序）
  'ops.field':        { path: '/ops/field',         icon: 'DatabaseOutlined', order: 0, group: 'other' },
  'ops.asset':        { path: '/ops/asset',         icon: 'FileOutlined', order: 1, group: 'other' },
  'ops.form':         { path: '/ops/form',          icon: 'FormOutlined', order: 2, group: 'other' },

  'order.refund': { path: '/order/refund', icon: 'RollbackOutlined', order: 5, group: 'mgt' },
  'fin.refund': { path: '/fin/refund', icon: 'RollbackOutlined', order: 1, group: 'mgt' },

  // ── 财务中心 ──
  'fin.offline': { path: '/fin/offline',   icon: 'AuditOutlined',   order: 0, group: 'mgt' },
  'fin.fee':  { path: '/fin/fee',         icon: 'MoneyCollectOutlined', order: 1, group: 'mgt' },
  'fin.txn':  { path: '/fin/transaction', icon: 'TransactionOutlined', order: 2, group: 'mgt' },
  'fin.stat': { path: '/fin/statistics',  icon: 'BarChartOutlined', order: 3, group: 'mgt' },
  'fin.rcp':  { path: '/fin/rcp',         icon: 'ProfileOutlined',    order: 4, group: 'mgt' },
  'fin.ar':   { path: '/fin/ar',          icon: 'TableOutlined',      order: 5, group: 'mgt' },

  // ── 设置中心 ──
  'sys.plan':      { path: '/sys/plan',          icon: 'ShoppingOutlined', order: 1, group: 'biz' },
  'sys.businessPresets': { path: '/sys/business-presets', icon: 'ToolOutlined', order: 3, group: 'biz' },
  'sys.pay':       { path: '/sys/payment',       icon: 'CreditCardOutlined', order: 2, group: 'biz' },
  'sys.trade':     { path: '/sys/trade',         icon: 'CalculatorOutlined', order: 4, group: 'biz' },
  'sys.invoice':   { path: '/sys/invoice',       icon: 'FileProtectOutlined', order: 5, group: 'biz' },
  'sys.dict':      { path: '/sys/dict',          icon: 'BookOutlined', order: 1, group: 'system' },
  'sys.i18n':      { path: '/sys/i18n',          icon: 'TranslationOutlined', order: 2, group: 'system' },
  'sys.role':      { path: '/sys/role',          icon: 'SafetyCertificateOutlined', order: 3, group: 'system' },
  // 权限包维护页走独立路径 `/iam/package`（10-09 §1.2）；菜单键仍归设置中心，路径与键不相等
  'sys.package':   { path: '/iam/package',       icon: 'AppstoreOutlined', order: 5, group: 'system' },
  'sys.admin':     { path: '/sys/admin',         icon: 'UserSwitchOutlined', order: 4, group: 'system' },
  'sys.notify':    { path: '/sys/notify',        icon: 'BellOutlined', order: 7, group: 'system' },
  // org.grant / org.staff 是后台帐号那两页（10-03-15-01）：iam 的 AdminLandingPage 已经
  // 按 iam.employee.view 把 /org/staff、/sys/grant 算进 reachableRoutes，侧边栏位置仍只认
  // 下方 ALWAYS_VISIBLE_MENU 那一条（useMenu 不按这里再挂一份：org.grant 键前缀是 org，
  // 按前缀挂会落进组织会务）；这里只给路径与图标
  'org.grant':     { path: '/sys/grant',         icon: 'SafetyOutlined', order: 5 },
  // 日志管理：PRD V2.5 把三张日志合成一页三页签（`sys.log`），旧三页保留为「直达某页签」
  // 的地址。侧栏只出合并页一项，页签级权限仍按下面三个旧键判（见 MENU_KEY_ALIAS）。
  'sys.log':       { path: '/sys/log',           icon: 'HistoryOutlined', order: 0, group: 'log' },
  'sys.log.login': { path: '/sys/log-login',     icon: 'LoginOutlined', order: 1, group: 'log' },
  'sys.log.op':    { path: '/sys/log-operation', icon: 'HistoryOutlined', order: 2, group: 'log' },
  'sys.log.err':   { path: '/sys/log-error',     icon: 'BugOutlined', order: 3, group: 'log' },
};

/**
 * 侧栏合并显示：旧三键（页签级权限）在侧栏只出合并页 `sys.log` 一项。
 *
 * **只影响展示**——`menus` 里保留的是服务端下发的原始键，所以合并页仍能用
 * `canEnter('sys.log.op')` 判某一个页签能不能看；`MENU_UI` 里的三条旧记录也保留，
 * `menuKeysOfRoutes` 还要靠它们把旧路由译回页签权限键。
 */
export const MENU_KEY_ALIAS: Record<string, string> = {
  'sys.log.login': 'sys.log',
  'sys.log.op': 'sys.log',
  'sys.log.err': 'sys.log',
};

export interface AlwaysVisibleMenuItem {
  /**
   * 菜单键，与 `MENU_UI` 同一套点分键；`permissionMatrix.ts` 里有它对应的页面码。
   * 名字只来自词条 `menu.<code>`。
   */
  code: string;
  path: string;
  icon: string;
  /** 挂到某个模块的某个分组下的排序；不填 group 则挂在模块根下 */
  order: number;
  /** 不填 = 顶层独立入口（目前只有工作台），不与任何模块同级分组 */
  module?: string;
  group?: string;
}

/**
 * 恒显清单：侧边栏里**不受 `menus ∩ MENU_UI` 交集规则约束**的菜单项，但它们仍要有页面码
 * 且该页可达才显示（工作台除外）——判据在 `useMenu`，页面码在 `permissionMatrix.ts`。
 *
 * 三类，都是设计意图，不是漏改：
 * ① 工作台 —— 设计文档 §5.5 引 PRD `home-1.md` R6：
 *    「工作台不单独授权：能进后台的人都看得到它」，是唯一的白名单
 * ② 订单中心四条 —— 服务端不下发 `order.*` 菜单键，但 `reachableRoutes` 会给出
 *    `/order/person`、`/order/club`、`/order/dunning`：它们各有一枚页面码，
 *    能不能显示按 `reachableRoutes` 译出的菜单键判。`order.create`（手动开单）仍是
 *    路由级占位、没有页面码，所以谁都看不到入口（超管可直接开 URL）。
 * ③ 后台帐号两页（org.staff / org.grant）—— 与 `MENU_UI` 里同名键共用页面码
 *    `iam.employee.view`，可达才显示；藏起来等于把 PRD B02/B03 两个建号入口一起藏掉，
 *    所以保留恒显位置，但不再无条件放出来。
 */
export const ALWAYS_VISIBLE_MENU: AlwaysVisibleMenuItem[] = [
  { code: 'home', path: '/home', icon: 'DashboardOutlined', order: 1 },

  // ── 订单中心：按 PRD 路由索引的对象口径（order.person 人员 / order.club 属会）──
  { code: 'order.person', path: '/order/person', icon: 'MoneyCollectOutlined', order: 1, module: 'order', group: 'mgt' },
  { code: 'order.club', path: '/order/club', icon: 'BankOutlined', order: 2, module: 'order', group: 'mgt' },
  { code: 'order.create', path: '/order/create', icon: 'FormOutlined', order: 3, module: 'order', group: 'mgt' },
  { code: 'order.dunning', path: '/order/dunning', icon: 'BellOutlined', order: 4, module: 'order', group: 'mgt' },

  // ── 后台帐号那两页（10-03-15-01）：能进不能进由 iam 会话（reachableRoutes）决定 ──
  // 它们必须有常显入口：sys.admin 卡头的「+ 职员帐号」跳 /org/staff、抽屉的建号入口也在这里，
  // 藏起来等于把 PRD B02/B03 两个入口一起藏掉。
  { code: 'org.staff', path: '/org/staff', icon: 'IdcardOutlined', order: 32, module: 'org', group: 'secretariat' },
  { code: 'org.grant', path: '/sys/grant', icon: 'SafetyOutlined', order: 6, module: 'sys', group: 'system' },
];

/** 菜单键 → 路由地址。登录后按 landingMenuKey 跳转用；恒显项（订单三页）也要能落地 */
export function pathOfMenuKey(key: string | null | undefined): string | null {
  if (!key) return null;
  if (MENU_UI[key]) return MENU_UI[key].path;
  return ALWAYS_VISIBLE_MENU.find((item) => item.code === key)?.path ?? null;
}

/**
 * 页面路由 → 菜单键。由 MENU_UI 与恒显清单一起反查：恒显项现在也按
 * `reachableRoutes` 判可达（订单三页不在 MENU_UI 里，只能靠这一份译回来）。
 */
const MENU_KEY_OF_PATH: Record<string, string> = Object.fromEntries([
  ...Object.entries(MENU_UI).map(([key, ui]) => [ui.path, key] as const),
  ...ALWAYS_VISIBLE_MENU.map((item) => [item.path, item.code] as const),
]);

/**
 * 服务端 `reachableRoutes`（此刻进得去的后台页面路由）→ 菜单键，只保留前端真的有页面的。
 *
 * 认不出的路由跳过而不是报错：那是「后端已经能进、前端还没建这一页」的正常状态
 * （一个岗位都不给时服务端回空清单，同样得到空结果——前端据此判定「一页都进不去」）。
 * 前端只做翻译，不在这里补默认值。
 */
export function menuKeysOfRoutes(routes: readonly string[] | null | undefined): string[] {
  return (routes ?? [])
    .map((route) => MENU_KEY_OF_PATH[route])
    .filter((key): key is string => !!key);
}
